/**
 * Core shared types for Minelunky.
 * Persistent meta-progression is intentionally separated from run-scoped inventory.
 */

/** Voxel material identifiers stored in the world Uint8Array. */
export enum BlockType {
  Air = 0,
  Dirt = 1,
  Stone = 2,
  Iron = 3,
  Gold = 4,
  /** Camp descent pad — walk onto it to enter level 1. */
  SurfacePortal = 5,
  /** Spelunky-style exit — walk into it to advance to the next level. */
  ExitDoor = 6,
  /** Unbreakable sealed floors / walls between levels. */
  Bedrock = 7,
  /** Floor shop stall — walk into it and press E to sell run materials. */
  Shop = 8,
}

/** High-level engine lifecycle states. */
export enum GameState {
  Loading = 'Loading',
  Menu = 'Menu',
  BaseCamp = 'BaseCamp',
  Diving = 'Diving',
  Respawning = 'Respawning',
  Paused = 'Paused',
}

/** Pickaxe tiers unlocked via meta-progression. */
export enum PickaxeTier {
  Rusty = 0,
  Iron = 1,
  Steel = 2,
  Carbide = 3,
  Plasma = 4,
}

export interface Vector3D {
  x: number;
  y: number;
  z: number;
}

export interface AABB {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** Runtime entity snapshot used by physics and networking. */
export interface EntityState {
  id: string;
  position: Vector3D;
  velocity: Vector3D;
  /** Half-extents of the entity AABB in world units. */
  halfExtents: Vector3D;
  onGround: boolean;
  alive: boolean;
  health: number;
  maxHealth: number;
}

/** Default player hit points. */
export const PLAYER_MAX_HEALTH = 100;

/**
 * Permanent account / save data. Survives death.
 * Wallet and unlocked tiers are never wiped by a cave wipeout.
 */
export interface PersistentPlayerProfile {
  playerId: string;
  displayName: string;
  walletCredits: number;
  unlockedPickaxeTiers: ReadonlySet<PickaxeTier>;
  equippedPickaxeTier: PickaxeTier;
  totalDeaths: number;
  totalExtractions: number;
  highestDepthReached: number;
  highestLevelReached: number;
}

/**
 * Materials gathered during the current cave descent.
 * Cleared on death; banked on successful extraction back at camp.
 */
export interface ActiveRunInventory {
  dirt: number;
  stone: number;
  iron: number;
  gold: number;
  /** 0 = base camp, 1..levelCount = dive stages. */
  currentLevel: number;
  depthReached: number;
}

export interface LevelBounds {
  /** Inclusive floor voxel Y (walkable bedrock / stone). */
  floorY: number;
  /** Inclusive ceiling voxel Y. */
  ceilingY: number;
  /** Entrance spawn (world space). */
  entrance: Vector3D;
  /** Exit door voxel coordinates. */
  exitDoor: { x: number; y: number; z: number };
  /** Shop stall voxel coordinates (bottom of 2-tall marker). */
  shop: { x: number; y: number; z: number };
  /** Boss arena kind string (EnemyKind), or null for normal maze levels. */
  bossKind: string | null;
  /** Spire totem pedestal voxel centers (boss arenas only). */
  bossPedestals?: Array<{ x: number; y: number; z: number }>;
}

export interface GameConfig {
  /** Fixed simulation rate in Hz (physics / logic). */
  readonly fixedTickRate: number;
  /** Maximum simulation steps per animation frame (spiral-of-death guard). */
  readonly maxFrameSteps: number;
  /** World dimensions in voxels. */
  readonly worldWidth: number;
  readonly worldHeight: number;
  readonly worldDepth: number;
  /** Top N layers treated as the safe surface base camp. */
  readonly surfaceCampLayers: number;
  /** Voxel height of each sealed dive level (including floor + ceiling). */
  readonly levelHeight: number;
  /** Number of Spelunky-style dive stages below camp. */
  readonly levelCount: number;
  /** Edge length of one voxel cube in world units. */
  readonly blockSize: number;
  /** Player AABB half-extents. */
  readonly playerHalfExtents: Vector3D;
  /** Horizontal acceleration while grounded / airborne. */
  readonly moveAcceleration: number;
  readonly maxHorizontalSpeed: number;
  readonly airControlMultiplier: number;
  readonly groundFriction: number;
  readonly airFriction: number;
  readonly gravity: number;
  /** Impulse applied on jump (positive Y). */
  readonly jumpVelocity: number;
  /** Terminal fall speed (negative Y). */
  readonly terminalVelocity: number;
  /**
   * Downward impact speed (positive magnitude) that triggers fall death.
   * Compared against |velocity.y| at the moment of ground contact.
   */
  readonly fallDamageThreshold: number;
  /** Mouse look sensitivity (radians per pixel). */
  readonly lookSensitivity: number;
  /** Max mining reach in world units. */
  readonly mineRange: number;
  /** Minimum ms between successful mine hits. */
  readonly mineCooldownMs: number;
  /** Seed for procedural cave generation. */
  readonly worldSeed: number;
}

export const DEFAULT_GAME_CONFIG: GameConfig = Object.freeze({
  fixedTickRate: 60,
  maxFrameSteps: 8,
  worldWidth: 48,
  worldHeight: 136,
  worldDepth: 48,
  surfaceCampLayers: 8,
  levelHeight: 24,
  levelCount: 5,
  blockSize: 1,
  playerHalfExtents: Object.freeze({ x: 0.35, y: 0.9, z: 0.35 }),
  moveAcceleration: 48,
  maxHorizontalSpeed: 6.5,
  airControlMultiplier: 0.35,
  groundFriction: 14,
  airFriction: 2,
  gravity: -28,
  jumpVelocity: 9.2,
  terminalVelocity: -42,
  fallDamageThreshold: 18,
  lookSensitivity: 0.0022,
  mineRange: 5,
  mineCooldownMs: 180,
  worldSeed: 0x4d494e45,
});

export function createEmptyRunInventory(): ActiveRunInventory {
  return {
    dirt: 0,
    stone: 0,
    iron: 0,
    gold: 0,
    currentLevel: 0,
    depthReached: 0,
  };
}

export function createDefaultProfile(playerId: string, displayName: string): PersistentPlayerProfile {
  return {
    playerId,
    displayName,
    walletCredits: 0,
    unlockedPickaxeTiers: new Set([PickaxeTier.Rusty]),
    equippedPickaxeTier: PickaxeTier.Rusty,
    totalDeaths: 0,
    totalExtractions: 0,
    highestDepthReached: 0,
    highestLevelReached: 0,
  };
}

export function vec3(x = 0, y = 0, z = 0): Vector3D {
  return { x, y, z };
}

export function copyVec3(out: Vector3D, src: Readonly<Vector3D>): Vector3D {
  out.x = src.x;
  out.y = src.y;
  out.z = src.z;
  return out;
}

export function setVec3(out: Vector3D, x: number, y: number, z: number): Vector3D {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
}

export function isMineable(type: BlockType): boolean {
  return (
    type === BlockType.Dirt ||
    type === BlockType.Stone ||
    type === BlockType.Iron ||
    type === BlockType.Gold
  );
}

/** Typed payload map for the engine event bus. */
export interface EngineEvents {
  'state:changed': { previous: GameState; next: GameState };
  'player:death': {
    playerId: string;
    deathLocation: Vector3D;
    cause: 'fall' | 'hazard' | 'combat' | 'unknown';
    impactSpeed: number;
  };
  'player:respawned': { playerId: string; spawnLocation: Vector3D };
  'player:damaged': { playerId: string; amount: number; health: number; maxHealth: number };
  'player:healed': { playerId: string; health: number; maxHealth: number };
  'enemy:killed': { enemyId: string; name: string };
  'enemy:spawned': { stage: number; count: number };
  'boss:defeated': { stage: number; name: string };
  'block:changed': { x: number; y: number; z: number; previous: BlockType; next: BlockType };
  'block:mined': { x: number; y: number; z: number; blockType: BlockType };
  'inventory:reset': { playerId: string };
  'inventory:changed': {
    source: 'mine' | 'craft' | 'extract' | 'death' | 'equip' | 'loot' | 'sell' | 'other';
  };
  'loot:dropped': { dropId: string; position: Vector3D; itemCount: number; stage: number };
  'loot:recovered': { dropId: string; itemCount: number };
  'craft:completed': { recipeId: string; resultItemId: string };
  'level:entered': { level: number; spawnLocation: Vector3D; fromDoor: boolean };
  'run:extracted': { level: number; inventory: ActiveRunInventory };
  'shop:sold': { itemId: string; amount: number; credits: number };
  'ui:toast': { message: string; kind: 'info' | 'warn' | 'success' };
  'world:ready': { width: number; height: number; depth: number; levelCount: number; seed: number };
  'network:remote-move': {
    playerId: string;
    position: Vector3D;
    velocity: Vector3D;
    yaw: number;
    health: number;
    maxHealth: number;
  };
  'network:remote-block': { x: number; y: number; z: number; blockType: BlockType };
  'network:remote-death': { playerId: string; deathLocation: Vector3D };
  'network:session': { code: string; role: 'host' | 'client' };
}

export type EngineEventName = keyof EngineEvents;
export type EngineEventHandler<E extends EngineEventName> = (payload: EngineEvents[E]) => void;

/** Marker entity spawned at death coordinates (drop-pod / gravestone). */
export interface DeathMarkerEntity {
  id: string;
  kind: 'drop-pod' | 'gravestone';
  position: Vector3D;
  ownerPlayerId: string;
  createdAt: number;
  /** True while the loot bag is still on the ground. */
  lootPending: boolean;
}

export interface VoxelHit {
  x: number;
  y: number;
  z: number;
  blockType: BlockType;
  /** Distance along the ray to the hit face. */
  distance: number;
  /** Face normal of the hit (unit axis). */
  normal: Vector3D;
}
